import { NextFontWithVariable } from 'next/dist/compiled/@next/font';
import { Instrument_Serif, Inter, Roboto_Mono } from 'next/font/google';
import localFont from 'next/font/local';

export const inter = Inter({
  subsets: ['latin'],
  variable: '--font-inter',
  display: 'swap',
});

export const instrument_serif = Instrument_Serif({
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-instrument-serif',
  weight: '400',
});

export const roboto_mono = Roboto_Mono({
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-roboto-mono',
});

const thicccboi = localFont({
  src: [
    {
      path: '../../../../public/assets/fonts/THICCCBOI-Bold.ttf',
      weight: '800',
      style: 'normal',
    },
  ],
  variable: '--font-thicccboi',
});

interface ThemeFont {
  label: string;
  nextFont: NextFontWithVariable;
  /**
   * CSS variable emitted by next/font. Only defined inside this dashboard
   * (see pages/_document.tsx), so it is used exclusively to preview the font
   * in the selector — never as the persisted theme value.
   */
  cssVariable: string;
  /**
   * CSS font-family stack persisted in the project theme. Hosted pages set it
   * verbatim as a `font-family` value, so it must be a valid stack (a bare
   * `--font-*` name is not) and must carry a generic fallback for renderers
   * that do not ship the webfont.
   */
  fontFamily: string;
}

export const themeFonts: ThemeFont[] = [
  {
    label: 'Inter',
    nextFont: inter,
    cssVariable: '--font-inter',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  {
    label: 'Instrument Serif',
    nextFont: instrument_serif,
    cssVariable: '--font-instrument-serif',
    fontFamily: "'Instrument Serif', Georgia, serif",
  },
  {
    label: 'Roboto Mono',
    nextFont: roboto_mono,
    cssVariable: '--font-roboto-mono',
    fontFamily: "'Roboto Mono', ui-monospace, monospace",
  },
  {
    label: 'Thicccboi',
    nextFont: thicccboi,
    cssVariable: '--font-thicccboi',
    fontFamily: 'THICCCBOI, system-ui, sans-serif',
  },
];

/**
 * Themes saved before this change stored the raw next/font CSS variable name
 * (e.g. `--font-inter`) instead of a font-family stack, which is not a valid
 * `font-family` value anywhere. Translate those legacy values on read so the
 * selector shows the saved font and the next save persists the correct shape.
 *
 * Unknown values are returned untouched: projects may hold a custom stack set
 * outside the dashboard, and we must not silently drop it.
 */
export const resolveThemeFontFamily = (storedValue: string | undefined | null): string => {
  if (!storedValue) return '';

  const legacyMatch = themeFonts.find((font) => font.cssVariable === storedValue);
  if (legacyMatch) return legacyMatch.fontFamily;

  return storedValue;
};

export const buildTypographyVariables = () => {
  return themeFonts.map(({ nextFont }) => nextFont.variable).join(' ');
};
